import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabaseApi, Project, Category } from '../services/supabase';
import { 
  FolderOpen, 
  ArrowLeft, 
  Save, 
  X,
  Loader
} from 'lucide-react';
import styled from 'styled-components';

const EditProjectContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
`;

const MainContent = styled.div`
  display: flex;
  min-height: calc(100vh - 70px);
`;

const Sidebar = styled.aside`
  width: 250px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 24px 0;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    color: #667eea;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
`;

const Form = styled.form`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f1f5f9;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  min-height: 48px;
  background: white;
`;

const Tag = styled.span`
  background: #e0e7ff;
  color: #3730a3;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveTag = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6366f1;
  padding: 0;
  
  &:hover {
    color: #dc2626;
  }
`;

const TagInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  flex: 1;
  min-width: 120px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.variant === 'secondary' ? '#e2e8f0' : 'transparent'};
  background: ${props => props.variant === 'secondary' ? 'white' : '#667eea'};
  color: ${props => props.variant === 'secondary' ? '#374151' : 'white'};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #dc2626;
`;

const EditProject: React.FC = () => {
    const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    category_id: '',
    image_url: '',
    technologies: [] as string[],
    features: [] as string[],
    client: '',
    duration: '',
    budget: '',
    status: 'completed',
    project_url: '',
    github_url: '',
    demo_url: ''
  });

  const [techInput, setTechInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    if (id) {
      fetchProject(id);
      fetchCategories();
    }
  }, [id]);

  const fetchProject = async (projectId: string) => {
    try {
      setLoading(true);
      const data = await supabaseApi.getProject(projectId);
      if (data) {
        setProject(data);
        setFormData({
          title: data.title,
          description: data.description,
          short_description: data.short_description || '',
          category_id: data.category_id,
          image_url: data.image_url || '',
          technologies: data.technologies || [],
          features: data.features || [],
          client: data.client || '',
          duration: data.duration || '',
          budget: data.budget || '',
          status: data.status,
          project_url: data.project_url || '',
          github_url: data.github_url || '',
          demo_url: data.demo_url || ''
        });
      } else {
        setError('Project not found');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await supabaseApi.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addTag = (type: 'technologies' | 'features', value: string, setInput: React.Dispatch<React.SetStateAction<string>>) => {
    if (value.trim() && !formData[type].includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        [type]: [...prev[type], value.trim()]
      }));
      setInput('');
    }
  };

  const removeTag = (type: 'technologies' | 'features', index: number) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    try {
      const success = await supabaseApi.updateProject(id, formData);
      if (success) {
        navigate('/projects');
      }
    } catch (error) {
      console.error('Error updating project:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <EditProjectContainer>
        <Header>
          <HeaderContent>
            <Logo>🚀 Portfolio Admin</Logo>
          </HeaderContent>
        </Header>
        <MainContent>
          <Sidebar>
            <NavItem onClick={() => navigate('/')}>
              <FolderOpen size={20} />
              Dashboard
            </NavItem>
            <NavItem onClick={() => navigate('/projects')}>
              <FolderOpen size={20} />
              Projects
            </NavItem>
            <NavItem onClick={() => navigate('/categories')}>
              <FolderOpen size={20} />
              Categories
            </NavItem>
          </Sidebar>
          <Content>
            <LoadingState>
              <Loader size={20} />
              Loading project...
            </LoadingState>
          </Content>
        </MainContent>
      </EditProjectContainer>
    );
  }

  if (error) {
    return (
      <EditProjectContainer>
        <Header>
          <HeaderContent>
            <Logo>🚀 Portfolio Admin</Logo>
          </HeaderContent>
        </Header>
        <MainContent>
          <Sidebar>
            <NavItem onClick={() => navigate('/')}>
              <FolderOpen size={20} />
              Dashboard
            </NavItem>
            <NavItem onClick={() => navigate('/projects')}>
              <FolderOpen size={20} />
              Projects
            </NavItem>
            <NavItem onClick={() => navigate('/categories')}>
              <FolderOpen size={20} />
              Categories
            </NavItem>
          </Sidebar>
          <Content>
            <ErrorState>
              <div>{error}</div>
              <Button
                variant="secondary"
                onClick={() => navigate('/projects')}
                style={{ marginTop: '16px' }}
              >
                Back to Projects
              </Button>
            </ErrorState>
          </Content>
        </MainContent>
      </EditProjectContainer>
    );
  }

  return (
    <EditProjectContainer>
      <Header>
        <HeaderContent>
          <Logo>🚀 Portfolio Admin</Logo>
        </HeaderContent>
      </Header>

      <MainContent>
        <Sidebar>
          <NavItem onClick={() => navigate('/')}>
            <FolderOpen size={20} />
            Dashboard
          </NavItem>
          <NavItem onClick={() => navigate('/projects')}>
            <FolderOpen size={20} />
            Projects
          </NavItem>
          <NavItem onClick={() => navigate('/categories')}>
            <FolderOpen size={20} />
            Categories
          </NavItem>
        </Sidebar>

        <Content>
          <PageHeader>
            <BackButton onClick={() => navigate('/projects')}>
              <ArrowLeft size={20} />
            </BackButton>
            <PageTitle>Edit Project</PageTitle>
          </PageHeader>

          <Form onSubmit={handleSubmit}>
            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Project Title *</Label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                    placeholder="Enter project title"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Category</Label>
                  <Select
                    value={formData.category_id}
                    onChange={(e) => handleInputChange('category_id', e.target.value)}
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Client</Label>
                  <Input
                    type="text"
                    value={formData.client}
                    onChange={(e) => handleInputChange('client', e.target.value)}
                    placeholder="Enter client name"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="completed">Completed</option>
                    <option value="in-progress">In Progress</option>
                    <option value="planning">Planning</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Duration</Label>
                  <Input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    placeholder="e.g., 3 months"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Budget</Label>
                  <Input
                    type="text"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    placeholder="e.g., $15,000"
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Description</SectionTitle>
              <FormGrid>
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>Short Description</Label>
                  <Textarea
                    value={formData.short_description}
                    onChange={(e) => handleInputChange('short_description', e.target.value)}
                    placeholder="Brief description (max 300 characters)"
                    maxLength={300}
                  />
                </FormGroup>

                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>Full Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    required
                    placeholder="Detailed project description"
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Technologies & Features</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Technologies</Label>
                  <TagsContainer>
                    {formData.technologies.map((tech, index) => (
                      <Tag key={index}>
                        {tech}
                        <RemoveTag onClick={() => removeTag('technologies', index)}>
                          <X size={14} />
                        </RemoveTag>
                      </Tag>
                    ))}
                    <TagInput
                      type="text"
                      value={techInput}
                      onChange={(e) => setTechInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag('technologies', techInput, setTechInput)}
                      placeholder="Add technology..."
                    />
                  </TagsContainer>
                </FormGroup>

                <FormGroup>
                  <Label>Features</Label>
                  <TagsContainer>
                    {formData.features.map((feature, index) => (
                      <Tag key={index}>
                        {feature}
                        <RemoveTag onClick={() => removeTag('features', index)}>
                          <X size={14} />
                        </RemoveTag>
                      </Tag>
                    ))}
                    <TagInput
                      type="text"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag('features', featureInput, setFeatureInput)}
                      placeholder="Add feature..."
                    />
                  </TagsContainer>
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Media & Links</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Project Image URL</Label>
                  <Input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => handleInputChange('image_url', e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Project URL</Label>
                  <Input
                    type="url"
                    value={formData.project_url}
                    onChange={(e) => handleInputChange('project_url', e.target.value)}
                    placeholder="https://project-demo.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>GitHub Repository</Label>
                  <Input
                    type="url"
                    value={formData.github_url}
                    onChange={(e) => handleInputChange('github_url', e.target.value)}
                    placeholder="https://github.com/username/repo"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Demo URL</Label>
                  <Input
                    type="url"
                    value={formData.demo_url}
                    onChange={(e) => handleInputChange('demo_url', e.target.value)}
                    placeholder="https://demo.project.com"
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormActions>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/projects')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : (
                  <>
                    <Save size={16} style={{ marginRight: '8px' }} />
                    Save Changes
                  </>
                )}
              </Button>
            </FormActions>
          </Form>
        </Content>
      </MainContent>
    </EditProjectContainer>
  );
};

export default EditProject;
